import { PropertyGridDialogProps } from './PropertyGridDialog';
import { getOrCreateDomElement } from '../layer/DialogUtils';
import { renderComponent, unMount } from '../../utils/RenderingUtils';
import { IAlertDialogProps } from './AlertDialog';
import { IConfirmDialogProps } from './ConfirmDialog/ConfirmDialog';
import { useDialogStore } from './DialogStore';

export { AlertDialog } from './AlertDialog';
export { Dialog } from './Dialog';
export { YesNoDialog } from './YesNoDialog';
export { EitherOrDialog } from './EitherOrDialog';
export { PropertyGridDialog } from './PropertyGridDialog';

export async function renderPropertyGridDialog(props: PropertyGridDialogProps) {
  // lazy load all that property-grid stuff
  const { PropertyGridDialog } = await import(/* webpackChunkName: "dp-cmp-property-grid" */ './PropertyGridDialog');
  const host = getOrCreateDomElement({ className: '', id: 'dp-property-grid-dialog-host' });

  if (!props.dialogProps) props.dialogProps = { open: true };
  const closeAction = props.dialogProps.onClose;
  props.dialogProps.onClose = () => {
    closeAction && closeAction();
    unMount(host, true);
  };

  await renderComponent({
    container: host,
    component: <PropertyGridDialog {...props} />,
    autoUnmount: true,
  });
}

export async function renderAlertOrInfoDialog(
  props: Omit<IAlertDialogProps, 'onClose' | 'open'>,
  callback?: () => void
) {
  const { AlertDialog } = await import('./AlertDialog');
  const host = getOrCreateDomElement({
    id: 'dp-alert-dialog-host',
  });

  // extend onClose
  const dialogProps = {
    ...props,
    open: true, // assume if they want it rendered, they want it open.
    onClose: () => {
      root.unmount();
      host.remove();
    },
  } as IAlertDialogProps;

  const root = await renderComponent({
    container: host,
    component: <AlertDialog {...dialogProps} />,
    autoUnmount: false,
    callback,
  });

  return root;
}

export async function renderConfirmDialog(
  { actionHandler: propsActionHandler, ...props }: IConfirmDialogProps,
  callback?: () => void
) {
  const { ConfirmDialog } = await import('./ConfirmDialog/ConfirmDialog');
  const host = getOrCreateDomElement({
    id: 'dp-confirm-dialog-host',
  });

  // extend onClose
  const dialogProps = {
    ...props,
    open: true, // assume if they want it rendered, they want it open.
    actionHandler: choice => {
      propsActionHandler(choice);
      root.unmount();
      host.remove();
    },
  } as IConfirmDialogProps;

  const root = await renderComponent({
    container: host,
    component: <ConfirmDialog {...dialogProps} />,
    autoUnmount: false,
    callback,
  });

  return root;
}

export function closeAllDialogs() {
  useDialogStore.getState().actions.closeAllDialogs();
}
